import type { GoalView } from '@aj/core';
import { Box, HStack, ProgressBar, Text, VStack } from '@aj/ui';

import { useT } from '../../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

/**
 * The destination — the first thing on Jatra, and the largest.
 *
 * The largest element is TEXT, not a numeral. Jatra answers "where am I
 * going"; opening it with an XP total answers "what is my score", which is a
 * different and worse question, and the one §53.8 exists to keep out.
 *
 * The bar and the pace line appear only when the goal has parts to count. A
 * goal like "get fit" has no honest denominator, and a day-elapsed ratio in
 * its place would fill up on its own whether or not the student did anything.
 */
export function GoalCard({ goal }: { goal: GoalView }) {
  const t = useT();


  return (
    <VStack gap={3}>
      <HStack gap={3} align="baseline" justify="between">
        <Text variant="heading" tone="ink" className="min-w-0">
          {goal.title}
        </Text>
        <Text variant="caption" tone="subtle" className="shrink-0">
          {t(`goal.cat.${goal.category}` as MessageKey)}
        </Text>
      </HStack>

      <HStack gap={3} align="baseline" className="flex-wrap">
        {goal.progress !== null && (
          <Text variant="label" tone="accent" numeric>
            {t('goal.partsOf', { done: goal.partsDone, total: goal.partsTotal })}
          </Text>
        )}
        <Text variant="caption" tone="muted" numeric>
          {t('home.daysLeft', { count: goal.daysLeft })}
        </Text>
      </HStack>

      {goal.progress !== null && (
        <ProgressBar value={goal.progress} size="sm" label={goal.title} />
      )}

      {/* Pace, never debt. It recomputes, so it has no wrong state — miss
          three days and it does not say the plan is broken, it says the pace
          tightened. Phrasing it as "a part every N days" is what keeps that
          true; "2 behind" would be the same number as an accusation. */}
      {goal.daysPerPart !== null && (
        <Text variant="caption" tone="subtle">
          {t('goal.pace', { days: Math.max(1, Math.round(goal.daysPerPart)) })}
        </Text>
      )}

      {/* The obstacle sits with the destination on purpose: MCII is explicit
          that a goal shown as its desired outcome alone is counterproductive
          rather than merely incomplete. */}
      {goal.obstacle !== undefined && (
        <Box className="rounded-lg bg-sunken px-3 py-2">
          <Text variant="caption" tone="muted">
            {t('goal.whenHard', { obstacle: goal.obstacle })}
          </Text>
        </Box>
      )}
    </VStack>
  );
}
